import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Bell, MessageCircle, Gift } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const params = filter !== "all" ? { type: filter } : {};
    api.get("/notifications", { params }).then(res => {
      setNotifications(res.data);
      setLoading(false);
    }).catch(() => { toast.error("Failed to load notifications"); setLoading(false); });
  }, [filter]);

  return (
    <div data-testid="notifications-page" className="space-y-6">
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">Notifications</h1>
          <p className="text-muted-foreground text-sm mt-1">WhatsApp notification history</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-[rgba(212,175,55,0.06)] border border-[rgba(212,175,55,0.15)] rounded-sm p-4 flex items-start gap-3">
        <MessageCircle className="w-5 h-5 gold-text mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-foreground font-medium">WhatsApp Integration (MOCKED)</p>
          <p className="text-xs text-muted-foreground mt-1">
            Notifications are currently mocked. Configure Twilio or Meta WhatsApp Cloud API credentials to enable real WhatsApp messaging.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">Filter:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="notification-filter" className="w-48 bg-card border-border rounded-sm h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="order_ready">Order Ready</SelectItem>
            <SelectItem value="birthday">Birthday Wishes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="luxury-card rounded-sm">
        <CardContent className="p-0">
          <Table className="luxury-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Customer</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Message</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Sent At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={5}><div className="h-8 bg-muted/20 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : notifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No notifications yet
                  </TableCell>
                </TableRow>
              ) : notifications.map((n) => (
                <TableRow key={n.id} data-testid={`notification-row-${n.id}`} className="border-border">
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] rounded-sm ${
                      n.type === "birthday" ? "bg-pink-500/10 text-pink-400 border-pink-500/25" : "bg-green-500/10 text-green-400 border-green-500/25"
                    }`}>
                      <span className="flex items-center gap-1">
                        {n.type === "birthday" ? <Gift className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
                        {n.type === "birthday" ? "Birthday" : "Order Ready"}
                      </span>
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm text-foreground">{n.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{n.customer_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <p className="text-sm text-muted-foreground truncate">{n.message}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] rounded-sm bg-amber-500/10 text-amber-400 border-amber-500/25">
                      MOCKED
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {n.sent_at ? format(parseISO(n.sent_at), "dd MMM yyyy, hh:mm a") : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
